import { AppCombobox, type AppComboboxOption } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { useQuery } from '@tanstack/react-query'
import { Check } from 'lucide-react'
import { useMemo } from 'react'
import { SALESFORCE_PROJECT_MEMBER_TYPE_IDS } from 'shared'
import { getSalesforceProjects, type SalesforceProject } from '@/lib'
import { queryKeys } from '@/lib/queryKeys'
import { cn } from '@/lib/utils'

/**
 * ProjectSelector コンポーネントの Props
 *
 * @param value - 選択中のプロジェクトID
 * @param onChange - プロジェクト変更ハンドラ
 * @param memberTypeId - メンバー種別ID（デフォルト: 1 キャプテン）
 * @param disabled - 無効化状態
 * @param placeholder - プレースホルダーテキスト
 */
interface ProjectSelectorProps {
  value: number | null
  onChange: (projectId: number | null, project: SalesforceProject | null) => void
  memberTypeId?: number
  disabled?: boolean
  placeholder?: string
  required?: boolean
  error?: string
}

/**
 * 案件選択コンポーネント
 *
 * Salesforceプロジェクトを検索・選択するためのオートコンプリート入力
 */
export function ProjectSelector({
  value,
  onChange,
  memberTypeId = SALESFORCE_PROJECT_MEMBER_TYPE_IDS.CAPTAIN,
  disabled = false,
  placeholder = 'プロジェクトを検索（ANOでの検索可）',
  required = false,
  error: externalError,
}: ProjectSelectorProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.salesforceProjects(memberTypeId),
    queryFn: async () => {
      return getSalesforceProjects({
        salesforceProjectMemberTypeId: memberTypeId,
      })
    },
  })

  const projects = data?.projects || []

  const selectedProject = useMemo(() => {
    return projects.find((p) => p.id === value) || null
  }, [projects, value])

  const options: AppComboboxOption[] = useMemo(() => {
    return projects.map((project) => ({
      value: project.id.toString(),
      label: `${project.name} ${project.customerName}`,
      data: project,
    }))
  }, [projects])

  const handleValueChange = (newValue: string, option?: AppComboboxOption) => {
    if (!newValue) {
      onChange(null, null)
      return
    }
    const projectId = Number.parseInt(newValue, 10)
    const project = (option?.data as SalesforceProject) || projects.find((p) => p.id === projectId) || null
    onChange(projectId === value ? null : projectId, projectId === value ? null : project)
  }

  const filterOption = (option: AppComboboxOption, searchQuery: string) => {
    if (!searchQuery) return true
    const query = searchQuery.toLowerCase()
    const project = option.data as SalesforceProject | undefined
    if (!project) return option.label.toLowerCase().includes(query)
    return (
      project.name.toLowerCase().includes(query) ||
      project.ano.toLowerCase().includes(query) ||
      project.customerName.toLowerCase().includes(query)
    )
  }

  const renderOption = (option: AppComboboxOption, isSelected: boolean) => {
    const project = option.data as SalesforceProject | undefined
    if (!project) return option.label

    return (
      <>
        <Check className={cn('mr-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')} />
        <div className='flex flex-col flex-1 min-w-0'>
          <div className='flex items-center gap-2'>
            <span className='font-medium truncate'>{project.name}</span>
            <span className='text-sm text-muted-foreground truncate'>{project.customerName}</span>
          </div>
          <div className='text-xs text-muted-foreground'>
            ANO: {project.ano} | ¥{project.totalAmount.toLocaleString()}
          </div>
        </div>
      </>
    )
  }

  const helpText = selectedProject
    ? `ANO: ${selectedProject.ano} ¥${selectedProject.totalAmount.toLocaleString()}`
    : undefined

  const fetchErrorMessage = error ? (error instanceof Error ? error.message : '案件の取得に失敗しました') : undefined
  const errorMessage = externalError || fetchErrorMessage

  return (
    <AppCombobox
      value={value?.toString() || ''}
      onValueChange={handleValueChange}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
      loading={isLoading}
      field={{ label: 'プロジェクト', required, helpText, error: errorMessage }}
      search={{
        placeholder: 'プロジェクト名またはANOで検索...',
        emptyMessage: projects.length === 0 ? 'プロジェクトがありません' : 'プロジェクトが見つかりません',
        filterFn: filterOption,
      }}
      customization={{ contentClassName: 'w-[600px]', renderOption }}
    />
  )
}
